import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { recordService } from "./record.service";
import type {
	RecordCountParams,
	RecordFileParams,
	RecordInstanceListParams,
	RecordNoteParams,
	RecordTypesAllParams,
	RecordTypesListParams,
} from "./record.types";

// ==================== QUERY KEYS ====================

export const recordKeys = {
	all: ["record"] as const,
	types: () => [...recordKeys.all, "record-types"] as const,
	geometrySources: () => [...recordKeys.all, "geometry-sources"] as const,
	gpsMethods: () => [...recordKeys.all, "gps-methods"] as const,
	typesAll: (params?: RecordTypesAllParams | RecordTypesListParams) =>
		[...recordKeys.types(), "all", params] as const,
	typesCount: (params?: RecordTypesListParams) =>
		[...recordKeys.types(), "count", params] as const,
	typesPage: (start: number, count: number, params?: RecordTypesListParams) =>
		[...recordKeys.types(), "page", start, count, params] as const,
	recordTypeDetail: (id: string, subOrgId?: string, orgId?: string) =>
		[...recordKeys.types(), "detail", id, subOrgId, orgId] as const,
	recordsCount: (params?: RecordCountParams) =>
		[...recordKeys.all, "records-count", params] as const,
	recordTypeHistory: (id: string) =>
		[...recordKeys.types(), "history", id] as const,
	recordTypeHistoryCount: (id: string) =>
		[...recordKeys.recordTypeHistory(id), "count"] as const,
	recordTypeHistoryList: (id: string, start: number, count: number) =>
		[...recordKeys.recordTypeHistory(id), "list", start, count] as const,
};

export const recordInstanceKeys = {
	all: ["record-instance"] as const,
	lists: () => [...recordInstanceKeys.all, "list"] as const,
	list: (start: number, count: number, params?: RecordInstanceListParams) =>
		[...recordInstanceKeys.lists(), start, count, params] as const,
	counts: () => [...recordInstanceKeys.all, "count"] as const,
	count: (params?: RecordCountParams) =>
		[...recordInstanceKeys.counts(), params] as const,
	details: () => [...recordInstanceKeys.all, "detail"] as const,
	detail: (recordId: string, organizationId?: string) =>
		[...recordInstanceKeys.details(), recordId, organizationId] as const,
	history: (recordId: string) =>
		[...recordInstanceKeys.all, "history", recordId] as const,
	historyCount: (recordId: string) =>
		[...recordInstanceKeys.history(recordId), "count"] as const,
	historyList: (recordId: string, start: number, count: number) =>
		[...recordInstanceKeys.history(recordId), "list", start, count] as const,
	files: (recordId: string) =>
		[...recordInstanceKeys.all, "files", recordId] as const,
	filesCount: (recordId: string, params?: RecordFileParams) =>
		[...recordInstanceKeys.files(recordId), "count", params] as const,
	filesList: (
		recordId: string,
		start: number,
		count: number,
		params?: RecordFileParams,
	) =>
		[
			...recordInstanceKeys.files(recordId),
			"list",
			start,
			count,
			params,
		] as const,
	fileContent: (recordId: string, fileId: string) =>
		[...recordInstanceKeys.files(recordId), "content", fileId] as const,
	fileThumbnail: (recordId: string, fileId: string) =>
		[...recordInstanceKeys.files(recordId), "thumbnail", fileId] as const,
	notes: (recordId: string) =>
		[...recordInstanceKeys.all, "notes", recordId] as const,
	notesCount: (recordId: string, params?: RecordNoteParams) =>
		[...recordInstanceKeys.notes(recordId), "count", params] as const,
	notesList: (
		recordId: string,
		start: number,
		count: number,
		params?: RecordNoteParams,
	) =>
		[
			...recordInstanceKeys.notes(recordId),
			"list",
			start,
			count,
			params,
		] as const,
};

// ==================== QUERY OPTIONS ====================

export const recordQueryOptions = {
	/**
	 * Geometry sources — GET /records/geometry-sources
	 */
	geometrySources: () =>
		queryOptions({
			queryKey: recordKeys.geometrySources(),
			queryFn: () => recordService.getGeometrySources(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * GPS methods — GET /records/gps-methods
	 */
	gpsMethods: () =>
		queryOptions({
			queryKey: recordKeys.gpsMethods(),
			queryFn: () => recordService.getGpsMethods(),
			staleTime: 30 * 60 * 1000,
			gcTime: 60 * 60 * 1000,
		}),

	/**
	 * Accepts either RecordTypesAllParams or RecordTypesListParams so callers
	 * can pass the same listParams object they use for count/page queries.
	 */
	typesAll: (input?: RecordTypesAllParams | RecordTypesListParams) =>
		queryOptions({
			queryKey: recordKeys.typesAll(input),
			queryFn: () =>
				recordService.getRecordTypes(input as RecordTypesAllParams),
		}),

	typesCount: (input?: RecordTypesListParams) =>
		queryOptions({
			queryKey: recordKeys.typesCount(input),
			queryFn: () => recordService.getRecordTypesCount(input),
			staleTime: 0,
			gcTime: 0,
		}),

	typesPage: (start: number, count: number, input?: RecordTypesListParams) =>
		queryOptions({
			queryKey: recordKeys.typesPage(start, count, input),
			queryFn: () => recordService.getRecordTypesPage(start, count, input),
			staleTime: 0,
			gcTime: 0,
		}),

	recordTypeDetail: (
		recordTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	) =>
		queryOptions({
			queryKey: recordKeys.recordTypeDetail(
				recordTypeId,
				options?.subOrganizationId,
				options?.organizationId,
			),
			queryFn: () => recordService.getRecordTypeById(recordTypeId, options),
			enabled: !!recordTypeId,
		}),

	/**
	 * Count of record instances for a given type/org.
	 * Used to gate delete actions — hide delete when count > 0.
	 */
	recordsCount: (params?: RecordCountParams) =>
		queryOptions({
			queryKey: recordKeys.recordsCount(params),
			queryFn: ({ signal }) =>
				recordService.getRecordsCount(params, { signal }),
			staleTime: 0,
			gcTime: 5 * 60 * 1000,
		}),

	recordTypeHistory: {
		count: (recordTypeId: string, organizationId?: string) =>
			queryOptions({
				queryKey: [
					...recordKeys.recordTypeHistoryCount(recordTypeId),
					organizationId,
				],
				queryFn: () =>
					recordService.getRecordTypeHistoryCount(recordTypeId, organizationId),
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!recordTypeId && recordTypeId.length > 0,
			}),

		infinite: (
			recordTypeId: string,
			pageSize: number = 10,
			totalCount: number,
			organizationId?: string,
		) =>
			infiniteQueryOptions({
				queryKey: [
					...recordKeys.recordTypeHistoryList(recordTypeId, 1, pageSize),
					"infinite",
					totalCount,
					organizationId,
				],
				queryFn: ({ pageParam }) =>
					recordService.getRecordTypeHistory(
						recordTypeId,
						pageParam,
						pageSize,
						organizationId,
					),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled:
					!!recordTypeId && recordTypeId.length > 0 && totalCount !== undefined,
			}),
	},
};

export const recordInstanceQueryOptions = {
	list: (start: number, count: number, params?: RecordInstanceListParams) =>
		queryOptions({
			queryKey: recordInstanceKeys.list(start, count, params),
			queryFn: () => recordService.listRecords(start, count, params),
		}),
	count: (params?: RecordCountParams) =>
		queryOptions({
			queryKey: recordInstanceKeys.count(params),
			queryFn: ({ signal }) =>
				recordService.getRecordsCount(params, { signal }),
			staleTime: 0,
			gcTime: 0,
		}),
	detail: (recordId: string, options?: { organizationId?: string }) =>
		queryOptions({
			queryKey: recordInstanceKeys.detail(recordId, options?.organizationId),
			queryFn: () => recordService.getRecord(recordId, options),
			enabled: !!recordId && recordId.length > 0,
		}),
	history: {
		count: (recordId: string) =>
			queryOptions({
				queryKey: recordInstanceKeys.historyCount(recordId),
				queryFn: () => recordService.getRecordHistoryCount(recordId),
			}),
		/**
		 * Infinite query for record history (newest first).
		 * @param totalCount - Required for reverse pagination
		 */
		infinite: (recordId: string, pageSize: number = 10, totalCount: number) =>
			infiniteQueryOptions({
				queryKey: [
					...recordInstanceKeys.historyList(recordId, 1, pageSize),
					"infinite",
					totalCount,
				],
				queryFn: ({ pageParam }) =>
					recordService.getRecordHistory(recordId, pageParam, pageSize),
				initialPageParam: totalCount
					? Math.max(1, totalCount - pageSize + 1)
					: 1,
				getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
					if (lastPageParam <= 1) return undefined;
					return Math.max(1, lastPageParam - pageSize);
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					if (!totalCount || firstPageParam >= totalCount - pageSize + 1) {
						return undefined;
					}
					return firstPageParam + pageSize;
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 10 * 60 * 1000,
				enabled: !!recordId && totalCount !== undefined,
			}),
	},
	files: {
		count: (
			recordId: string,
			params?: RecordFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.filesCount(recordId, params),
				queryFn: () =>
					recordService.getRecordFilesCount(recordId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!recordId && recordId.length > 0,
			}),
		list: (
			recordId: string,
			start: number = 1,
			count: number = 50,
			params?: RecordFileParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.filesList(recordId, start, count, params),
				queryFn: () =>
					recordService.getRecordFiles(recordId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!recordId && recordId.length > 0,
			}),
		content: (
			recordId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.fileContent(recordId, fileId),
				queryFn: () => recordService.getRecordFile(recordId, fileId, options),
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!recordId && !!fileId,
			}),
		thumbnail: (
			recordId: string,
			fileId: string,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.fileThumbnail(recordId, fileId),
				queryFn: async () => {
					try {
						return await recordService.getRecordFileThumbnail(
							recordId,
							fileId,
							options,
						);
					} catch {
						return null;
					}
				},
				staleTime: 5 * 60 * 1000,
				gcTime: 30 * 60 * 1000,
				enabled: !!recordId && !!fileId,
				retry: false,
			}),
	},
	notes: {
		count: (
			recordId: string,
			params?: RecordNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.notesCount(recordId, params),
				queryFn: () =>
					recordService.getRecordNotesCount(recordId, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!recordId && recordId.length > 0,
			}),
		list: (
			recordId: string,
			start: number = 1,
			count: number = 50,
			params?: RecordNoteParams,
			options?: { organizationId?: string },
		) =>
			queryOptions({
				queryKey: recordInstanceKeys.notesList(recordId, start, count, params),
				queryFn: () =>
					recordService.getRecordNotes(recordId, start, count, params, options),
				staleTime: 30 * 1000,
				gcTime: 5 * 60 * 1000,
				enabled: !!recordId && recordId.length > 0,
			}),
	},
};
